<template>
  <BaseModal
    is-open
    :title="modalTitle"
    size="small"
    @close="close"
  >
    <template v-if="isCheckInboxMode">
      <div class="text-sm">
        If you have an account with us, we've sent a sign-in link to:
      </div>

      <div class="bg-grey-50 rounded-lg p-4 mt-6 mb-3 text-sm font-semibold text-center break-words" v-text="form.email.value" />

      <div class="text-sm text-center">
        <span v-if="isResendCooldownActive" class="text-grey-700">Request a new link in {{ resendCooldownSeconds }} seconds</span>
        <a v-else class="font-semibold cursor-pointer" @click="onResendClick">Resend email</a>
      </div>

      <HorizontalLine text="or" class="my-6" />

      <div class="text-sm text-center">
        <a class="font-semibold cursor-pointer" @click="switchToPassword">Sign in with password</a>
      </div>
    </template>

    <template v-else>
      <div class="text-sm" v-text="topText" />

      <div class="flex gap-4 flex-col my-6">
        <GoogleLoginButton :api-config="googleApiConfig" :tracking-params="{...trackingParams, auth_method: 'google'}" text="Log in with Google" @click="onGoogleLoginClick" @success="onSocialSuccess" @error="setError" />
        <AppleLoginButton v-if="appleApiConfig && appleApiConfig.clientId" :api-config="appleApiConfig" :tracking-params="{...trackingParams, auth_method: 'apple'}" text="Log in with Apple" @click="onAppleLoginClick" @success="onSocialSuccess" @error="setError" />
        <FacebookLoginButton :api-config="apiConfig" :tracking-params="{...trackingParams, auth_method: 'facebook'}" text="Log in with Facebook" @click="onFacebookLoginClick" @success="onSocialSuccess" @error="setError" />
      </div>

      <HorizontalLine text="or" class="my-5" />

      <EmailField
        v-model="form.email.value"
        :errors="form.email.errors"
        placeholder="Email address"
        @update:modelValue="form.email.errors = []"
      />

      <template v-if="isPasswordMode">
        <TextField
          v-model="form.password.value"
          :icon="LockIcon"
          :errors="form.password.errors"
          :is-marked-as-invalid="form.password.errors.length > 0"
          name="password"
          type="password"
          :maxlength="64"
          class="mb-1.5"
          placeholder="Password"
          @update:modelValue="form.password.errors = []"
        />
        <a target="_blank" :href="forgotPasswordUrl" class="font-semibold" @click="onForgotLinkClick">
          Forgot your password?
        </a>

        <BaseButton color="green" :is-loading="isLoading" class="w-full mt-4" @click="login">
          Log in
        </BaseButton>

        <div class="text-sm text-center mt-4">
          Or <a class="font-semibold cursor-pointer" @click="switchToMagicLink">sign in with email</a> instead.
        </div>
      </template>

      <template v-else>
        <BaseButton color="green" :is-loading="isLoading" class="w-full" @click="sendMagicLink">
          Continue
        </BaseButton>

        <div class="text-sm text-center mt-4 leading-5">
          We will email you a link for a password-free sign-on. Or <a class="font-semibold cursor-pointer" @click="switchToPassword">sign in with password.</a>
        </div>
      </template>

      <div v-if="signUp" class="im:-mb-4 text-center leading-5">
        <HorizontalLine class="my-6" />
        Don't have a User Account? <a class="font-semibold cursor-pointer" @click="onSignUpClick">Sign up</a>
      </div>
    </template>
  </BaseModal>

  <Captcha :config="captcha" @onTokenResponse="(token) => { captcha.token = token; }" />
</template>
<script>
import LockIcon from '@tagvenue/icons/24/line/security/lock-01.svg';
import { useCountdown, useIntervalFn } from '@vueuse/core';
import { markRaw, ref } from 'vue';

import EmailField from '@/components/Forms/EmailField.vue';
import AppleLoginButton from '@/components/Ui/Buttons/AppleLoginButton.vue';
import BaseButton from '@/components/Ui/Buttons/BaseButton.vue';
import FacebookLoginButton from '@/components/Ui/Buttons/FacebookLoginButton.vue';
import GoogleLoginButton from '@/components/Ui/Buttons/GoogleLoginButton.vue';
import TextField from '@/components/Ui/Forms/TextField.vue';
import HorizontalLine from '@/components/Ui/HorizontalLine.vue';
import BaseModal from '@/components/Ui/Modals/BaseModal.vue';
import Captcha from '@/modules/public/enquiry-form/components/EnquiryForm/components/Captcha.vue';
import formValidation, { FORM_VALIDATION } from '@/services/formValidation';
import { csrfToken } from '@/utils';
import request from '@/utils/request';

const MODE = {
  PASSWORD: 'password',
  MAGIC_LINK: 'magicLink',
  CHECK_INBOX: 'checkInbox',
};

const RESEND_COOLDOWN_SECONDS = 60;
const AUTH_STATUS_POLL_INTERVAL_MS = 2000;

export default {
  name: 'WelcomeBackLoginModal',
  emits: ['close', 'trackEvent', 'login', 'error', 'signUpClick'],
  mixins: [formValidation],
  components: {
    TextField,
    EmailField,
    FacebookLoginButton,
    GoogleLoginButton,
    AppleLoginButton,
    BaseButton,
    Captcha,
    HorizontalLine,
    BaseModal,
  },
  props: {
    // eslint-disable-next-line vue/require-default-prop
    email: {
      type: String,
      required: false,
    },
    forgotPasswordUrl: {
      type: String,
      required: true,
    },
    magicLinkRedirectUrl: {
      type: String,
      default: '',
    },
    signUp: {
      type: Boolean,
      default: false,
    },
    trackingParams: {
      type: Object,
      required: true,
    },
    apiConfig: {
      type: Object,
      required: true,
    },
    googleApiConfig: {
      type: Object,
      required: true,
    },
    appleApiConfig: {
      type: Object,
      required: false,
      default: null,
    },
    // eslint-disable-next-line vue/require-default-prop
    topText: {
      type: String,
    },
  },
  setup() {
    const {
      remaining: resendCooldownSeconds,
      isActive: isResendCooldownActive,
      start: startResendCooldown,
      stop: stopResendCooldown,
    } = useCountdown(RESEND_COOLDOWN_SECONDS);

    const isLoggedInAsClient = ref(false);
    const {
      resume: startAuthStatusPolling,
      pause: stopAuthStatusPolling,
    } = useIntervalFn(() => {
      request('/auth-data/status')
        .then((data) => {
          isLoggedInAsClient.value = data.type === 'user';
        })
        .catch(() => {});
    }, AUTH_STATUS_POLL_INTERVAL_MS, { immediate: false });

    return {
      resendCooldownSeconds,
      isResendCooldownActive,
      startResendCooldown,
      stopResendCooldown,
      isLoggedInAsClient,
      startAuthStatusPolling,
      stopAuthStatusPolling,
    };
  },
  data() {
    return {
      mode: MODE.MAGIC_LINK,
      isLoading: false,
      LockIcon: markRaw(LockIcon),
      form: {
        email: {
          value: this.email || '',
          errors: [],
        },
        password: {
          value: '',
          errors: [],
        },
      },
      captcha: {
        sitekey: this.apiConfig?.captcha.sitekey,
        action: 'login',
        isEnabled: false,
        token: null,
      },
      formValidation: {
        email: {
          [FORM_VALIDATION.REQUIRED]: 'Please enter your email address',
          [FORM_VALIDATION.EMAIL]: 'Please enter a valid email address',
        },
        password: {
          [FORM_VALIDATION.REQUIRED]: 'Please enter your password',
        },
      },
    };
  },
  computed: {
    isPasswordMode() {
      return this.mode === MODE.PASSWORD;
    },
    isCheckInboxMode() {
      return this.mode === MODE.CHECK_INBOX;
    },
    modalTitle() {
      return this.isCheckInboxMode ? 'Check your inbox' : 'Welcome back';
    },
  },
  watch: {
    isLoggedInAsClient(isLoggedIn) {
      if (isLoggedIn) {
        this.onMagicLinkLoginDetected();
      }
    },
  },
  methods: {
    onSocialSuccess(response) {
      this.isLoading = true;

      this.$emit('login', { emailMatch: this.email === response?.email });
    },
    onForgotLinkClick() {
      this.trackEvent('forgot_password_link');
    },
    onFacebookLoginClick() {
      this.trackEvent('continue_with_facebook_button');
    },
    onGoogleLoginClick() {
      this.trackEvent('continue_with_google_button');
    },
    onAppleLoginClick() {
      this.trackEvent('continue_with_apple_button');
    },
    trackEvent(element, interaction = 'click') {
      this.$emit('trackEvent', { section: 'login_modal', element, interaction });
    },
    close() {
      this.isLoading = false;
      this.stopAuthStatusPolling();
      this.stopResendCooldown();
      this.trackEvent('close_button');
      this.$emit('close');
    },
    setError(error) {
      this.$emit('error', error);
      this.isLoading = false;
    },
    switchToMagicLink() {
      this.trackEvent('email_me_a_link_button');
      this.form.password.errors = [];
      this.mode = MODE.MAGIC_LINK;
    },
    switchToPassword() {
      this.trackEvent('sign_in_with_password_button');
      this.stopAuthStatusPolling();
      this.stopResendCooldown();
      this.mode = MODE.PASSWORD;
    },
    sendMagicLink() {
      this.trackEvent('sign_in_with_email_button');

      if (!this.validateField('email')) {
        return;
      }

      this.isLoading = true;
      this.requestMagicLink()
        .then(() => {
          this.mode = MODE.CHECK_INBOX;
          this.startResendCooldown();
          this.startAuthStatusPolling();
        })
        .catch((response) => this.handleMagicLinkError(response))
        .finally(() => {
          this.isLoading = false;
        });
    },
    onResendClick() {
      this.trackEvent('resend_email_button');
      this.startResendCooldown();
      this.requestMagicLink().catch((response) => {
        // Nothing was sent, so let the user retry right away; a 429 keeps the cooldown as the server asked.
        if (response?.status !== 429) {
          this.stopResendCooldown();
        }

        this.handleMagicLinkError(response);
      });
    },
    requestMagicLink() {
      return request('/login/client/magic-link/send', {
        method: 'POST',
        data: {
          email: this.form.email.value,
          redirectUrl: this.getMagicLinkRedirectUrl(),
        },
      });
    },
    handleMagicLinkError(response) {
      if (response?.status === 429) {
        this.setError('Too many attempts. Try again later.');
        return;
      }

      if (response?.status === 422) {
        response.json().then(({ errors }) => this.showEmailErrors(errors?.email));
        return;
      }

      this.setError('An error occurred. Sorry for the inconvenience.');
    },
    showEmailErrors(errors) {
      if (!errors) {
        this.setError('An error occurred. Sorry for the inconvenience.');
        return;
      }

      this.stopAuthStatusPolling();
      this.stopResendCooldown();
      this.form.email.errors = errors;
      this.mode = MODE.MAGIC_LINK;
    },
    getMagicLinkRedirectUrl() {
      // A link sent to a different address than the one the parent knows about signs into another
      // account, so the parent's context-specific landing page would not belong to that user.
      if (this.email && this.form.email.value !== this.email) {
        return '';
      }

      return this.magicLinkRedirectUrl;
    },
    onMagicLinkLoginDetected() {
      this.stopAuthStatusPolling();
      this.stopResendCooldown();
      this.isLoading = true;

      this.$emit('login', { emailMatch: this.email === this.form.email.value });
    },
    login({ captcha = false } = {}) {
      const self = this;

      this.trackEvent('login_button');

      if (!this.validateFields()) {
        return;
      }

      this.isLoading = true;
      $.ajax({
        url: '/login/client',
        type: 'POST',
        hideDefaultErrorMessage: true,
        data: {
          'g-recaptcha-response': this.captcha.token,
          _token: csrfToken(),
          email: this.form.email.value,
          password: this.form.password.value,
          trackingParams: this.trackingParams,
        },
        success(data, type, xhr) {
          this.isLoading = false;

          if (xhr.status === 200) {
            self.$emit('login', { emailMatch: self.email === self.form.email.value });
          } else {
            self.setError('An error occurred. Sorry for the inconvenience.');
          }
        },
        error(response) {
          // reCAPTCHA Enterprise tokens are single-use; drop the consumed token so a retry mints a fresh one.
          self.captcha.token = null;

          if (!captcha && response.responseJSON?.['g-recaptcha-response']) {
            self.captcha.isEnabled = true;
            console.log('Captcha requested');
            Rollbar.debug('Captcha requested');

            const unwatch = self.$watch('captcha.token', () => {
              self.isLoading = false;
              self.captcha.isEnabled = false;
              self.login({ captcha: true });
              unwatch();
            });

            return;
          } if (response.responseJSON.password) {
            self.form.password.errors = response.responseJSON.password;
          } else if (response.responseJSON.email) {
            self.form.email.errors = response.responseJSON.email;
          } else if (response.responseJSON.message) {
            self.setError(response.responseJSON.message);
          } else {
            self.setError('An error occurred. Sorry for the inconvenience.');
          }

          self.isLoading = false;
        },
      });
    },
    onSignUpClick() {
      this.$emit('signUpClick', this.form.email.value);
    },
  },
  mounted() {
    this.trackEvent('modal', 'visible');
  },
};
</script>
